import { useCallback, useMemo, useState } from "react";
import {
    FlatList,
    type FlatListProps,
    type ListRenderItem,
    Pressable,
    SectionList,
    type SectionListProps,
    type SectionListRenderItem,
    StyleSheet,
    View,
} from "react-native";
import type { SectionListData } from "react-native/Libraries/Lists/SectionList";
import { SearchInput } from "~/components/form/SearchInput";
import { ListSeparator } from "~/components/layout/ListSeparator";
import { BaseText, Subtitle } from "~/components/layout/text";
import { NoRecords } from "~/components/layout/text/NoRecords";
import { Only } from "~/components/permission/Only";
import { Flex, Gap, Items, Padding } from "~/constants/layout";
import { Space } from "~/constants/sizing";
import { useTheme } from "~/providers/ThemeProvider";
import { Badge } from "../feedback/Badge";

export type Group<TItem> = {
    name: string;
    title?: string;
    matches: (item: TItem) => boolean;
    hideTab?: boolean;
};

type Tab = {
    title: string;
    key: string;
    count: number;
    selected: boolean;
};

type FilterWithSearch<TItem> = {
    withoutSearch?: false;
    searchWith: (item: TItem, term: string) => boolean;
    searchPlaceholder?: string;
};

type FilterWithoutSearch = {
    withoutSearch: true;
    searchWith?: never;
    searchPlaceholder?: never;
};

type Section<TItem> = {
    title: string;
    data: TItem[];
};

const ALL_GROUP = "__all";

export type FilteredListProps<TItem> = {
    data: TItem[];
    groups?: Group<TItem>[];
    renderItem: ListRenderItem<TItem>;
    refreshWith?: () => Promise<unknown>;
    isRefreshing?: boolean;
    withoutTabs?: boolean;
    sectionListProps?: Omit<SectionListProps<TItem, Section<TItem>>, "refreshing" | "sections">;
    listEmptyText?: string;
} & (FilterWithSearch<TItem> | FilterWithoutSearch) &
    FlatListProps<TItem>;

export function FilteredList<TItem>({
    data,
    groups,
    withoutSearch,
    searchWith,
    searchPlaceholder,
    renderItem,
    withoutTabs,
    refreshWith,
    isRefreshing,
    sectionListProps,
    listEmptyText,
    ItemSeparatorComponent = ListSeparator,
    ...listProps
}: FilteredListProps<TItem>) {
    const { border } = useTheme();
    const [group, setGroup] = useState<string>(ALL_GROUP);
    const [search, setSearch] = useState<string>("");
    const [refreshing, setRefreshing] = useState(false);
    const currently_refreshing = isRefreshing || refreshing;

    const doRefresh = useCallback(async () => {
        if (currently_refreshing || !refreshWith) {
            return;
        }

        setRefreshing(true);

        refreshWith().finally(() => setRefreshing(false));
    }, [currently_refreshing, refreshWith]);

    const render_section_item: SectionListRenderItem<TItem, Section<TItem>> = sectionListProps?.renderItem ??
    (renderItem as unknown as SectionListRenderItem<TItem, Section<TItem>>);

    const searchTerm = useMemo(() => {
        return withoutSearch ? "" : search.trim();
    }, [search, withoutSearch]);

    const currentGroup = useMemo(() => {
        return (groups ?? []).find((g) => g.name === group);
    }, [group, groups]);

    const filterSearch = useCallback(
        (item: TItem) => {
            if (withoutSearch || !searchTerm) {
                return true;
            }

            return searchWith(item, searchTerm);
        },
        [withoutSearch, searchTerm, searchWith],
    );

    const filter = useCallback(
        (item: TItem) => {
            if (!currentGroup || currentGroup.matches(item)) {
                return filterSearch(item);
            }

            return false;
        },
        [currentGroup, filterSearch],
    );

    const tabs: Tab[] = useMemo(() => {
        const visibleGroups = groups?.filter(({ hideTab }) => !hideTab && !withoutTabs);

        if (!visibleGroups?.length) {
            return [];
        }

        const all = data.filter(filterSearch);

        return [
            { title: "All", count: all.length, key: ALL_GROUP, selected: group === ALL_GROUP },
            ...visibleGroups.map(({ name, matches, title }) => ({
                title: title ?? name,
                count: all.filter(matches).length,
                key: name,
                selected: group === name,
            })),
        ];
    }, [groups, withoutTabs, filterSearch, data, group]);

    const hasSections = useMemo(() => Boolean(groups && group === ALL_GROUP), [groups, group]);

    const filteredData = useMemo(() => {
        return data.filter(filter);
    }, [data, filter]);

    const sectionData = useMemo(() => {
        if (!hasSections || !groups) {
            return [];
        }

        return groups
            .map(({ title, name, matches }) => ({
                title: title ?? name,
                data: filteredData.filter(matches),
            }))
            .filter((g) => g.data.length > 0);
    }, [hasSections, filteredData, groups]);

    const noRecords = listProps.ListEmptyComponent ?? <NoRecords emptyText={listEmptyText} />;

    const renderSectionHeader =
        sectionListProps?.renderSectionHeader ??
        (({ section }: { section: SectionListData<TItem, Section<TItem>> }) => (
            <Subtitle style={Padding.vertical_sm}>{section.title}</Subtitle>
        ));

    return (
        <View style={[Flex.flex, Gap.md]}>
            <Only not when={withoutSearch}>
                <SearchInput
                    hideLabel
                    label={searchPlaceholder ?? "Search"}
                    value={search}
                    onChangeText={setSearch}
                    placeholder={searchPlaceholder ?? "Search"}
                />
            </Only>
            <Only when={tabs}>
                <View style={[Flex.row, Items.center, Flex.evenly]} accessibilityRole="tablist">
                    {tabs.map((tab) => (
                        <Pressable
                            key={tab.key}
                            onPress={() => setGroup(tab.key)}
                            style={[styles.tab, tab.selected ? border.selected : border.secondary]}
                            accessibilityRole="tab"
                        >
                            <BaseText size="md" weight="semibold" colour={tab.selected ? "selected" : "secondary"}>
                                {tab.title}
                            </BaseText>
                            <Badge
                                title={tab.count}
                                variant={tab.selected ? "info" : "neutral"}
                                rounding="full"
                                size="sm"
                            />
                        </Pressable>
                    ))}
                </View>
            </Only>
            {hasSections ? (
                <SectionList<TItem, Section<TItem>>
                    {...sectionListProps}
                    scrollEventThrottle={50}
                    style={[styles.list, listProps.style]}
                    sections={sectionData}
                    ListEmptyComponent={noRecords}
                    renderItem={render_section_item}
                    renderSectionHeader={renderSectionHeader}
                    refreshing={currently_refreshing}
                    ItemSeparatorComponent={ItemSeparatorComponent}
                    onRefresh={doRefresh}
                />
            ) : (
                <FlatList<TItem>
                    {...listProps}
                    scrollEventThrottle={50}
                    style={[styles.list, listProps.style]}
                    data={filteredData}
                    renderItem={renderItem}
                    ListEmptyComponent={noRecords}
                    refreshing={currently_refreshing}
                    ItemSeparatorComponent={ItemSeparatorComponent}
                    onRefresh={doRefresh}
                />
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    tab: {
        flexGrow: 1,
        flexShrink: 1,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: Space.xs,
        borderWidth: 0,
        borderBottomWidth: 2,
        padding: Space.md,
    },
    list: {
        flex: 1,
    },
});
